/* ============================================================
   LUMIÈRE PARIS — Theme JavaScript (vanilla, no dependencies)
   Cart AJAX API, drawer, sticky ATC, header, gallery, variants,
   wishlist, bundle builder, popup, scroll reveal.
   ============================================================ */
(function () {
  "use strict";

  const money = cents =>
    window.Shopify && window.Shopify.formatMoney
      ? window.Shopify.formatMoney(
          cents,
          window.themeSettings && window.themeSettings.moneyFormat
        )
      : "$" + (cents / 100).toFixed(2);

  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ─────────────────────────────────────────────
     Scroll reveal (.fade-up → .visible)
     ───────────────────────────────────────────── */
  function initReveal() {
    const els = $$(".fade-up:not(.visible)");
    if (!("IntersectionObserver" in window) || !els.length) {
      els.forEach(e => e.classList.add("visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach(e => io.observe(e));
  }

  /* ─────────────────────────────────────────────
     Header: scrolled state, mobile menu, search
     ───────────────────────────────────────────── */
  function initHeader() {
    const header = $("[data-header]");
    if (header) {
      const onScroll = () =>
        header.classList.toggle("scrolled", window.scrollY > 60);
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
    }
    $$("[data-toggle]").forEach(btn => {
      on(btn, "click", () => {
        const target = $("#" + btn.getAttribute("data-toggle"));
        if (target) {
          const open = target.classList.toggle("open");
          btn.setAttribute("aria-expanded", String(open));
          if (open) {
            const input = target.querySelector(
              'input[type="search"], input[type="text"]'
            );
            if (input) setTimeout(() => input.focus(), 50);
          }
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     Wishlist (localStorage only — no backend)
     ───────────────────────────────────────────── */
  const Wishlist = {
    key: "lumiere-wishlist",
    get() {
      try {
        const raw = JSON.parse(localStorage.getItem(this.key)) || [];
        // Normalise: legacy entries were plain id strings.
        return raw
          .map(it => (typeof it === "string" ? { id: it } : it))
          .filter(it => it && it.id != null)
          .map(it => Object.assign({}, it, { id: String(it.id) }));
      } catch (e) {
        return [];
      }
    },
    save(list) {
      try {
        localStorage.setItem(this.key, JSON.stringify(list));
      } catch (e) {
        /* storage full / disabled — ignore */
      }
    },
    has(id) {
      id = String(id);
      return this.get().some(it => it.id === id);
    },
    toggle(data) {
      const id = String(data.id);
      const list = this.get();
      const idx = list.findIndex(it => it.id === id);
      if (idx === -1) list.push(data);
      else list.splice(idx, 1);
      this.save(list);
      return idx === -1;
    },
    remove(id) {
      id = String(id);
      this.save(this.get().filter(it => it.id !== id));
    },
  };

  const TRASH_SVG =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

  function paintHeart(btn, active) {
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-pressed", String(active));
    const heart = btn.querySelector("svg");
    if (heart) {
      heart.setAttribute("fill", active ? "var(--lumiere-blush)" : "none");
      heart.setAttribute(
        "stroke",
        active ? "var(--lumiere-blush)" : "currentColor"
      );
    }
  }

  // Reflect a product's saved state on every heart button for that id.
  function syncHearts(id, active) {
    id = String(id);
    $$('[data-wishlist="' + id + '"]').forEach(b => paintHeart(b, active));
  }

  /* Slide-out wishlist drawer — rendered client-side from localStorage. */
  const WishlistDrawer = {
    drawer: null,
    overlay: null,
    bound: false,
    init() {
      this.drawer = $("[data-wishlist-drawer]");
      this.overlay = $("[data-wishlist-overlay]");
      this.render();
      if (this.bound) return;
      this.bound = true;
      $$("[data-wishlist-open]").forEach(b =>
        on(b, "click", e => {
          e.preventDefault();
          this.open();
        })
      );
      $$("[data-wishlist-close]").forEach(b =>
        on(b, "click", () => this.close())
      );
      on(this.overlay, "click", () => this.close());
      on(document, "keydown", e => {
        if (e.key === "Escape") this.close();
      });
      if (this.drawer) {
        on(this.drawer, "click", e => {
          const rm = e.target.closest("[data-wishlist-remove]");
          if (rm) {
            const id = rm.getAttribute("data-wishlist-remove");
            Wishlist.remove(id);
            syncHearts(id, false);
            this.render();
          }
        });
      }
    },
    open() {
      if (typeof Cart !== "undefined" && Cart.close) Cart.close();
      if (this.overlay) this.overlay.classList.add("open");
      if (this.drawer) {
        this.drawer.classList.add("open");
        this.drawer.setAttribute("aria-hidden", "false");
      }
      document.body.style.overflow = "hidden";
    },
    close() {
      if (this.overlay) this.overlay.classList.remove("open");
      if (this.drawer) {
        this.drawer.classList.remove("open");
        this.drawer.setAttribute("aria-hidden", "true");
      }
      document.body.style.overflow = "";
    },
    render() {
      const items = Wishlist.get();
      $$("[data-wishlist-count]").forEach(el => {
        el.textContent = items.length;
        el.style.display = items.length > 0 ? "" : "none";
      });
      const wrap = $("[data-wishlist-items]");
      if (!wrap) return;
      const empty = $("[data-wishlist-empty]");
      if (!items.length) {
        wrap.innerHTML = "";
        if (empty) empty.style.display = "";
        return;
      }
      if (empty) empty.style.display = "none";
      const esc = s => String(s || "").replace(/[<>&"]/g, c =>
        ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]));
      wrap.innerHTML = items
        .map(it => {
          const url = esc(it.url) || "#";
          const img = it.image
            ? '<img src="' + esc(it.image) + '" alt="" width="64" height="76" style="width:64px;height:76px;object-fit:cover;flex-shrink:0;">'
            : '<span style="width:64px;height:76px;background:var(--lumiere-cream);flex-shrink:0;display:block;"></span>';
          return (
            '<div class="flex gap-4 bordered-b" style="padding-bottom:1.25rem;">' +
              '<a href="' + url + '" style="flex-shrink:0;">' + img + "</a>" +
              '<div class="flex-1" style="min-width:0;">' +
                '<div class="flex justify-between items-start mb-1">' +
                  '<a href="' + url + '" class="font-display text-sm leading-snug" style="font-weight:500;">' + esc(it.title) + "</a>" +
                  '<button type="button" data-wishlist-remove="' + esc(it.id) + '" class="header-icon-btn shrink-0" aria-label="Remove" style="margin-left:.5rem;">' + TRASH_SVG + "</button>" +
                "</div>" +
                (it.price ? '<p class="font-label text-sm mb-2" style="font-weight:700;color:var(--lumiere-charcoal);">' + esc(it.price) + "</p>" : "") +
                '<a href="' + url + '" class="btn-blush" style="padding:.4rem .875rem;font-size:.6rem;display:inline-block;">View product</a>' +
              "</div>" +
            "</div>"
          );
        })
        .join("");
    },
  };

  function initWishlist() {
    $$("[data-wishlist]").forEach(btn => {
      const id = btn.getAttribute("data-wishlist");
      paintHeart(btn, Wishlist.has(id));
      if (btn.dataset.wlBound) return;
      btn.dataset.wlBound = "1";
      on(btn, "click", e => {
        e.preventDefault();
        e.stopPropagation();
        const added = Wishlist.toggle({
          id: id,
          handle: btn.getAttribute("data-wishlist-handle") || "",
          url: btn.getAttribute("data-wishlist-url") || "",
          title: btn.getAttribute("data-wishlist-title") || "",
          image: btn.getAttribute("data-wishlist-image") || "",
          price: btn.getAttribute("data-wishlist-price") || "",
        });
        syncHearts(id, added);
        btn.classList.add("heart-pulse");
        setTimeout(() => btn.classList.remove("heart-pulse"), 400);
        WishlistDrawer.render();
        if (added && typeof Cart !== "undefined" && Cart.flash) {
          Cart.flash("Saved to your wishlist");
        }
      });
    });
    WishlistDrawer.init();
  }

  /* ─────────────────────────────────────────────
     Cart: AJAX add / change / drawer render
     ───────────────────────────────────────────── */
  const Cart = {
    drawer: null,
    overlay: null,

    init() {
      this.drawer = $("[data-cart-drawer]");
      this.overlay = $("[data-cart-overlay]");
      $$("[data-cart-open]").forEach(b =>
        on(b, "click", e => {
          e.preventDefault();
          this.open();
        })
      );
      $$("[data-cart-close]").forEach(b => on(b, "click", () => this.close()));
      on(this.overlay, "click", () => this.close());
      on(document, "keydown", e => {
        if (e.key === "Escape") this.close();
      });

      // Delegate qty + remove inside the drawer
      if (this.drawer) {
        on(this.drawer, "click", e => {
          const change = e.target.closest("[data-cart-change]");
          const remove = e.target.closest("[data-cart-remove]");
          if (change) {
            const key = change.getAttribute("data-key");
            const qty = parseInt(change.getAttribute("data-cart-change"), 10);
            this.change(key, qty);
          } else if (remove) {
            this.change(remove.getAttribute("data-key"), 0);
          }
        });
      }
    },

    open() {
      if (this.overlay) this.overlay.classList.add("open");
      if (this.drawer) {
        this.drawer.classList.add("open");
        this.drawer.setAttribute("aria-hidden", "false");
      }
      document.body.style.overflow = "hidden";
    },
    close() {
      if (this.overlay) this.overlay.classList.remove("open");
      if (this.drawer) {
        this.drawer.classList.remove("open");
        this.drawer.setAttribute("aria-hidden", "true");
      }
      document.body.style.overflow = "";
    },

    async add(items, openAfter) {
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(Array.isArray(items) ? { items: items } : items),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          this.flash(err.description || "Could not add to bag.");
          return null;
        }
        await this.refresh();
        if (openAfter !== false) this.open();
        return await res.json();
      } catch (e) {
        this.flash("Network error — please try again.");
        return null;
      }
    },

    async change(key, qty) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      await this.refresh();
    },

    async refresh() {
      // Re-render the cart drawer + bubble via the Section Rendering API
      try {
        const res = await fetch("/?sections=cart-drawer");
        const data = await res.json();
        if (data["cart-drawer"]) {
          const tmp = document.createElement("div");
          tmp.innerHTML = data["cart-drawer"];
          const fresh = tmp.querySelector("[data-cart-drawer-inner]");
          const current = $("[data-cart-drawer-inner]");
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        }
      } catch (e) {
        /* fall back to count fetch */
      }

      try {
        const cart = await (await fetch("/cart.js")).json();
        $$("[data-cart-count]").forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? "" : "none";
        });
      } catch (e) {
        /* noop */
      }
    },

    flash(msg) {
      let t = $("[data-toast]");
      if (!t) {
        t = document.createElement("div");
        t.setAttribute("data-toast", "");
        t.style.cssText =
          "position:fixed;bottom:1.5rem;left:50%;transform:translateX(-50%);background:var(--lumiere-charcoal);color:#fff;padding:.75rem 1.25rem;font-family:var(--font-body);font-size:.8rem;z-index:80;box-shadow:0 4px 24px rgba(0,0,0,.2)";
        document.body.appendChild(t);
      }
      t.textContent = msg;
      t.style.opacity = "1";
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => {
        t.style.opacity = "0";
      }, 2600);
    },
  };

  function initAddForms() {
    $$("[data-product-form]").forEach(form => {
      on(form, "submit", async e => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const requireVariant = form.querySelector('[name="id"]');
        if (requireVariant && !requireVariant.value) {
          Cart.flash("Please select all options.");
          return;
        }
        const label = btn ? btn.innerHTML : "";
        if (btn) {
          btn.disabled = true;
        }
        const formData = new FormData(form);
        const obj = {};
        formData.forEach((v, k) => {
          obj[k] = v;
        });
        const result = await Cart.add(obj);
        if (btn) {
          btn.disabled = false;
          if (result) {
            btn.innerHTML = "✓ Added to Bag";
            setTimeout(() => {
              btn.innerHTML = label;
            }, 2200);
          }
        }
      });
    });

    // Quick-add buttons on product cards (single variant)
    $$("[data-quick-add]").forEach(btn => {
      on(btn, "click", async e => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.getAttribute("data-quick-add");
        if (!id) return;
        btn.disabled = true;
        await Cart.add({ id: id, quantity: 1 });
        btn.disabled = false;
      });
    });
  }

  /* ─────────────────────────────────────────────
     Product gallery (thumbnail switch)
     ───────────────────────────────────────────── */
  function initGallery() {
    $$("[data-gallery]").forEach(gallery => {
      const main = gallery.querySelector("[data-gallery-main]");
      const thumbs = $$("[data-gallery-thumb]", gallery);
      thumbs.forEach(thumb => {
        on(thumb, "click", () => {
          const src = thumb.getAttribute("data-src");
          const srcset = thumb.getAttribute("data-srcset");
          if (main && src) {
            main.src = src;
            if (srcset) main.srcset = srcset;
          }
          thumbs.forEach(t => t.classList.remove("active"));
          thumb.classList.add("active");
        });
      });
    });
  }

  /* ─────────────────────────────────────────────
     Variant selection (size/colour → variant id)
     ───────────────────────────────────────────── */
  function initVariants() {
    $$("[data-variant-picker]").forEach(picker => {
      const form =
        picker.closest("[data-product-form]") ||
        document.querySelector("[data-product-form]");
      let variants = [];
      try {
        variants = JSON.parse(
          picker.querySelector("[data-variants-json]").textContent
        );
      } catch (e) {
        return;
      }

      const idInput = form ? form.querySelector('[name="id"]') : null;
      const priceEl = $("[data-product-price]");
      const compareEl = $("[data-product-compare]");
      const selected = {};

      function pick() {
        const optionNames = Object.keys(selected);
        const match = variants.find(v =>
          optionNames.every(n => v.options[parseInt(n, 10)] === selected[n])
        );
        const btn = form ? form.querySelector("[data-atc]") : null;
        if (match) {
          if (idInput) idInput.value = match.id;
          if (priceEl) priceEl.textContent = money(match.price);
          if (compareEl) {
            if (
              match.compare_at_price &&
              match.compare_at_price > match.price
            ) {
              compareEl.textContent = money(match.compare_at_price);
              compareEl.style.display = "";
            } else {
              compareEl.style.display = "none";
            }
          }
          if (btn) {
            btn.disabled = !match.available;
            btn.innerHTML = match.available
              ? btn.getAttribute("data-label") || "Add to Bag"
              : "Sold Out";
          }
        } else if (btn) {
          btn.disabled = true;
        }
      }

      $$("[data-option-index]", picker).forEach(control => {
        const idx = control.getAttribute("data-option-index");
        on(control, "click", e => {
          const opt = e.target.closest("[data-value]");
          if (!opt) return;
          selected[idx] = opt.getAttribute("data-value");
          $$("[data-value]", control).forEach(o =>
            o.classList.remove("active")
          );
          opt.classList.add("active");
          const labelOut = picker.querySelector("[data-selected-" + idx + "]");
          if (labelOut) labelOut.textContent = selected[idx];
          pick();
        });
        // preselect first available
        const first = control.querySelector("[data-value]");
        if (first) {
          selected[idx] = first.getAttribute("data-value");
          first.classList.add("active");
        }
      });
      pick();
    });
  }

  /* ─────────────────────────────────────────────
     Quantity steppers
     ───────────────────────────────────────────── */
  function initQty() {
    $$("[data-qty]").forEach(wrap => {
      const input = wrap.querySelector("input");
      wrap.querySelectorAll("[data-qty-step]").forEach(btn => {
        on(btn, "click", () => {
          const step = parseInt(btn.getAttribute("data-qty-step"), 10);
          const val = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = val;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });
  }

  /* ─────────────────────────────────────────────
     Sticky ATC (show when main ATC scrolled away)
     ───────────────────────────────────────────── */
  function initStickyAtc() {
    const sticky = $("[data-sticky-atc]");
    const anchor = $("[data-atc-anchor]");
    if (!sticky || !anchor || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle("visible", !entry.isIntersecting),
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  /* ─────────────────────────────────────────────
     Bundle builder (frequently-bought-together)
     ───────────────────────────────────────────── */
  function initBundle() {
    const bundle = $("[data-bundle]");
    if (!bundle) return;
    const base = parseInt(bundle.getAttribute("data-base-price"), 10) || 0;
    const baseId = bundle.getAttribute("data-base-id");
    const summary = $("[data-bundle-summary]", bundle);
    const totalEl = $("[data-bundle-total]", bundle);
    const wasEl = $("[data-bundle-was]", bundle);
    const saveEl = $("[data-bundle-save]", bundle);
    const discEl = $("[data-bundle-discount]", bundle);
    const addBtn = $("[data-bundle-add]", bundle);
    const selected = new Set();

    function update() {
      let raw = base;
      selected.forEach(el => {
        raw += parseInt(el.getAttribute("data-price"), 10) || 0;
      });
      const rate = selected.size >= 2 ? 0.1 : selected.size === 1 ? 0.05 : 0;
      const final = Math.round(raw * (1 - rate));
      if (summary) summary.style.display = selected.size ? "" : "none";
      if (totalEl) totalEl.textContent = money(final);
      if (wasEl) wasEl.textContent = money(raw);
      if (saveEl) saveEl.textContent = money(raw - final);
      if (discEl) discEl.textContent = rate * 100 + "% off";
    }

    $$("[data-bundle-item]", bundle).forEach(item => {
      on(item, "click", () => {
        const pressed = item.getAttribute("aria-pressed") === "true";
        item.setAttribute("aria-pressed", String(!pressed));
        item.classList.toggle("selected", !pressed);
        if (pressed) selected.delete(item);
        else selected.add(item);
        update();
      });
    });

    on(addBtn, "click", async () => {
      const items = [{ id: baseId, quantity: 1 }];
      selected.forEach(el =>
        items.push({ id: el.getAttribute("data-variant"), quantity: 1 })
      );
      await Cart.add(items);
    });
    update();
  }

  /* ─────────────────────────────────────────────
     Email popup (delayed, localStorage dismissal)
     ───────────────────────────────────────────── */
  function initPopup() {
    const popup = $("[data-popup]");
    if (!popup) return;
    const key = "lumiere-popup-dismissed";
    const delay = parseInt(popup.getAttribute("data-delay"), 10) || 8000;
    const close = () => {
      popup.classList.remove("open");
      try {
        localStorage.setItem(key, "1");
      } catch (e) {}
    };
    $$("[data-popup-close]", popup).forEach(b => on(b, "click", close));
    on(popup, "click", e => {
      if (e.target === popup) close();
    });
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(key) === "1";
    } catch (e) {}
    if (!dismissed) {
      setTimeout(() => popup.classList.add("open"), delay);
    }
  }

  /* ─────────────────────────────────────────────
     Sort dropdown (collection)
     ───────────────────────────────────────────── */
  function initSort() {
    $$("[data-sort-toggle]").forEach(btn => {
      const menu = btn.parentElement.querySelector("[data-sort-menu]");
      on(btn, "click", () => menu && menu.classList.toggle("open"));
    });
    $$("[data-sort-value]").forEach(opt => {
      on(opt, "click", () => {
        const value = opt.getAttribute("data-sort-value");
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", value);
        url.searchParams.delete("page");
        window.location.href = url.toString();
      });
    });
  }

  /* ─────────────────────────────────────────────
     Filters panel toggle (collection)
     ───────────────────────────────────────────── */
  function initFilters() {
    const drawer = $("[data-filter-drawer]");
    if (!drawer) return;
    const overlay = $("[data-filter-overlay]");
    const triggers = $$("[data-filter-toggle]");

    const open = () => {
      if (overlay) overlay.classList.add("open");
      drawer.classList.add("open");
      drawer.setAttribute("aria-hidden", "false");
      if (overlay) overlay.setAttribute("aria-hidden", "false");
      triggers.forEach(b => b.setAttribute("aria-expanded", "true"));
      document.body.style.overflow = "hidden";
    };
    const close = () => {
      if (overlay) overlay.classList.remove("open");
      drawer.classList.remove("open");
      drawer.setAttribute("aria-hidden", "true");
      if (overlay) overlay.setAttribute("aria-hidden", "true");
      triggers.forEach(b => b.setAttribute("aria-expanded", "false"));
      document.body.style.overflow = "";
    };

    triggers.forEach(b =>
      on(b, "click", e => {
        e.preventDefault();
        open();
      })
    );
    $$("[data-filter-close]").forEach(b => on(b, "click", close));
    if (overlay) on(overlay, "click", close);
    on(document, "keydown", e => {
      if (e.key === "Escape" && drawer.classList.contains("open")) close();
    });
  }

  /* ─────────────────────────────────────────────
     Mobile nav (slide-out drawer)
     ───────────────────────────────────────────── */
  function initMobileNav() {
    const drawer = $("[data-mobile-drawer]");
    if (!drawer) return;
    const overlay = $("[data-mobile-overlay]");
    const triggers = $$("[data-mobile-open]");

    const setExpanded = v =>
      triggers.forEach(b => b.setAttribute("aria-expanded", String(v)));

    const open = () => {
      if (overlay) overlay.classList.add("open");
      drawer.classList.add("open");
      drawer.setAttribute("aria-hidden", "false");
      setExpanded(true);
      document.body.style.overflow = "hidden";
    };
    const close = () => {
      if (overlay) overlay.classList.remove("open");
      drawer.classList.remove("open");
      drawer.setAttribute("aria-hidden", "true");
      setExpanded(false);
      document.body.style.overflow = "";
    };

    triggers.forEach(b =>
      on(b, "click", e => {
        e.preventDefault();
        open();
      })
    );
    $$("[data-mobile-close]").forEach(b => on(b, "click", close));
    if (overlay) on(overlay, "click", close);
    on(document, "keydown", e => {
      if (e.key === "Escape") close();
    });
    // Tapping a nav link closes the drawer before navigating.
    on(drawer, "click", e => {
      if (e.target.closest("a")) close();
    });
  }

  /* ─────────────────────────────────────────────
     Boot
     ───────────────────────────────────────────── */
  function boot() {
    initReveal();
    initHeader();
    initMobileNav();
    initWishlist();
    Cart.init();
    initAddForms();
    initGallery();
    initVariants();
    initQty();
    initStickyAtc();
    initBundle();
    initPopup();
    initSort();
    initFilters();
  }

  window.LumiereCart = Cart;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener("shopify:section:load", boot);
})();
